import React, { useEffect } from "react";
import { FaMailBulk, FaWhatsapp, FaArrowRight } from "react-icons/fa";
import TextEdit from "../../molecules/TextEdit";

import { useSignal } from "@preact/signals-react";
import useFetch from "../../../hooks/useFetch";
import { Variable } from "./BatchNotifcationForm";
import ErrorMessage from "../../atoms/ErrorMessage";
import { toast } from "react-toastify";
import appConfig from "../../../config/app-config";

const COMMUNICATION_ENV = appConfig.apis.communicationApiEnv;
const NOTIFICATION_URL = `${COMMUNICATION_ENV.address}/notifications`;
const API_KEY = COMMUNICATION_ENV.key;

enum MediaType {
    WPP = "WHATSAPP",
    MAIL = "EMAIL",
}

type Notification = {
    type_scheduled: string;
    operation: string;
    maxAttempts: number;
    media: MediaType;
    status: string;
    createdAt: string;
    sentAt: string;
    externalId: string;
    contactName: string;
    isPhoneValid: boolean;
    isEmailValid: boolean;
    phone: string;
    email: string;
    cc: string | null;
    bcc: string | null;
    attempts: number;
    errorMessage: string;
    variables: Variable[];
    templateName: string;
    canDispatch: boolean;
    subject?: string; // Optional for email notifications
    from?: string; // Optional for email notifications
    replyTo?: string; // Optional for email notifications
}

interface NotificationPageViewProps {
    typeScheduled: string;
    targetDate: string;
    operation: string;
    onClose?: () => void;
}

const NotificationPageView: React.FC<NotificationPageViewProps> = ({
    typeScheduled,
    targetDate,
    operation,
    onClose
}) => {
    const id = `${typeScheduled}:${targetDate}:${operation}`;

    const apiVersion = useSignal('');
    const isLoadingForm = useSignal(false);
    const notification = useSignal<Notification>({
        type_scheduled: "",
        operation: "",
        maxAttempts: 0,
        media: MediaType.WPP,
        status: "",
        createdAt: "",
        sentAt: "",
        externalId: "",
        contactName: "",
        isPhoneValid: false,
        isEmailValid: false,
        phone: "",
        email: "",
        cc: null,
        bcc: null,
        attempts: 0,
        errorMessage: "",
        variables: [],
        templateName: "",
        canDispatch: false,
    });

    const { isLoading, data, error, isTokenError } = useFetch(`${NOTIFICATION_URL}/${id}/page-view`, [{ key: 'x-api-key', value: API_KEY }]);

    // TODO not found

    useEffect(() => {
        if (isTokenError && onClose) {
            onClose();
        }
        if (error && onClose) {
            onClose();
        }
    }, [isTokenError, error, onClose]);

    useEffect(() => {
        if (data) {
            apiVersion.value = `${data.apiName} - ${data.apiVersion}`;

            const { template } = data;
            const variables: Variable[] = [];
            template.variables.forEach((variable: any) => {
                if (variable.format !== "email" && variable.format !== "phone" && variable.name !== "FULL_NAME") {
                    const value = data.notification.variables[variable.field] || "";

                    variables.push({
                        field: variable.display,
                        value: value,
                        format: variable.format,
                    });
                }
            });

            let subject = "";
            let from = "";
            let replyTo = "";

            if (data.notification.media === "EMAIL") {
                const [innerTemplate] = template.templates;
                subject = innerTemplate.subject.replace(/{{(.*?)}}/g, (_: string, key: string): string => {
                    const variable = (template.variables.find((v: any) => v.name === key)) as Variable;
                    return data.notification.variables[variable.field] || "";
                });

                from = innerTemplate.from;
                replyTo = innerTemplate.replyTo;
            }

            notification.value = {
                ...notification.value,
                type_scheduled: data.notification.type_scheduled,
                operation: data.notification.operation,
                maxAttempts: data.notification.maxAttempts,
                media: data.notification.media,
                status: data.notification.status,
                createdAt: data.notification.createdAt,
                sentAt: data.notification.sentAt,
                externalId: data.notification.externalId,
                contactName: data.notification.contactName,
                isPhoneValid: data.notification.isPhoneValid,
                isEmailValid: data.notification.isEmailValid,
                phone: data.notification.phone,
                email: data.notification.email,
                attempts: data.notification.attempts,
                errorMessage: data.notification.errorMessage,
                variables: variables,
                templateName: template.description,
                canDispatch: data.notification.canDispatch,
                subject,
                from,
                replyTo,
            };
        }
    }, [data]);

    //TODO mover para libs ou componente especifico
    const getContentByStatus = (status: string) => {
        const statusTable = {
            "PENDING": "Notificação pendente",
            "CHECKING": "Checando status da notificação",
            "SENT": "Notificação enviada",
            "ERROR": "Erro ao enviar notificação",
            "RECEIVED": "Notificação recebida",
            "DELIVERED": "Notificação entregue",
            "FAILED": "Falha ao enviar notificação",
            "CONSUMED": "Visualizado pelo destinatário",
        };

        return statusTable[status as keyof typeof statusTable] || "Status desconhecido";
    };

    const getCssColorBy = (status: string) => {

        const statusTable = {
            "PENDING": "bg-slate-400 dark:bg-slate-600",
            "CHECKING": "bg-slate-500 dark:bg-slate-700",
            "SENT": "bg-green-500 dark:bg-green-700",
            "ERROR": "bg-red-500 dark:bg-red-700",
            "RECEIVED": "bg-blue-500 dark:bg-blue-700",
            "DELIVERED": "bg-blue-500 dark:bg-blue-700",
            "CONSUMED": "bg-green-700 dark:bg-green-900",
            "FAILED": "bg-red-600 dark:bg-red-800",
        };

        return statusTable[status as keyof typeof statusTable] || "bg-gray-500 dark:bg-gray-700";
    };

    const dispatch = async () => {
        isLoadingForm.value = true;
        try {
            const response = await fetch(`${COMMUNICATION_ENV.address}/dispatch`, {
                method: 'POST',
                headers: {
                    'x-api-key': API_KEY || '',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    notificationType: typeScheduled,
                    targetDate,
                    service: '',
                    operations: [notification.value.operation],
                }),
            });
            if (response.ok) {
                toast.success('Notificação iniciada com sucesso!');
                onClose?.();
            } else {
                toast.error('Erro ao iniciar notificação!');
            }
        } catch (error) {
            toast.error('Ocorreu um erro na comunicação com o servidor!\nTente novamente mais tarde.');
        }
        isLoadingForm.value = false;
    };

    return (
        <div className="p-3 bg-white dark:bg-slate-800 rounded-lg h-full overflow-y-auto">
            {isLoading ? (
                <div className="flex items-center justify-center h-full">
                    <div className="flex flex-col items-center space-y-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
                        <span className="text-sm text-gray-500 dark:text-gray-400">Carregando detalhes...</span>
                    </div>
                </div>
            ) : (
                <>
                    <div className="flex items-center justify-between mb-4 ml-2">
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {notification.value.contactName}
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                #{notification.value.operation}
                            </p>
                        </div>
                        {notification.value.canDispatch && (
                            <button
                                disabled={isLoadingForm.value}
                                className="flex items-center text-white bg-[#FF9119] hover:bg-[#FF9119]/80 font-medium rounded text-sm px-3 py-2"
                                onClick={dispatch}
                            >
                                Notificar
                                <FaArrowRight className="ml-2" />
                            </button>
                        )}
                    </div>

                    <div className="space-y-4">
                        <div className="p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    Tentativas: {notification.value.attempts}/{notification.value.maxAttempts}
                                </span>
                                {notification.value.media === MediaType.WPP && (
                                    <FaWhatsapp className="text-xl text-green-700" />
                                )}
                                {notification.value.media === MediaType.MAIL && (
                                    <FaMailBulk className="text-xl text-blue-700" />
                                )}
                            </div>
                            <span className={`${getCssColorBy(notification.value.status)} text-white text-xs font-medium px-2 py-1 rounded-sm`}>
                                {notification.value.status}
                            </span>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                {getContentByStatus(notification.value.status)}
                            </p>
                        </div>

                        {notification.value.errorMessage && (
                            <ErrorMessage message={notification.value.errorMessage} />
                        )}

                        <div className="space-y-3">
                            <TextEdit label="Tipo:" value={notification.value.templateName} disabled />
                            <TextEdit label="Data:" value={targetDate?.split("-").reverse().join("/")} disabled />

                            {notification.value.media === MediaType.WPP && (
                                <TextEdit label="Telefone:" value={notification.value.phone} disabled prefix="+55" />
                            )}

                            {notification.value.media === MediaType.MAIL && (
                                <>
                                    <TextEdit label="Email:" value={notification.value.email} disabled />
                                    <TextEdit label="Assunto:" value={notification.value.subject} disabled />
                                    <TextEdit label="De:" value={notification.value.from} disabled />
                                    {notification.value.replyTo && (
                                        <TextEdit label="Responder:" value={notification.value.replyTo} disabled />
                                    )}
                                    {notification.value.cc && (
                                        <TextEdit label="Cc:" value={notification.value.cc} disabled />
                                    )}
                                    {notification.value.bcc && (
                                        <TextEdit label="Bcc:" value={notification.value.bcc} disabled />
                                    )}
                                </>
                            )}

                            {notification.value.variables.map((variable, index) => (
                                <TextEdit
                                    key={index}
                                    label={`${variable.field}:`}
                                    value={variable.value}
                                    disabled
                                />
                            ))}

                            {data?.notification?.variables?.items && data.notification.variables.items.length > 0 && (
                                <GroupedItemsTable items={data.notification.variables.items} />
                            )}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

// Componente de tabela com paginação para items agrupados
const GroupedItemsTable: React.FC<{ items: any[] }> = ({ items }) => {
    const currentPage = useSignal(1);
    const itemsPerPage = 100;

    const totalPages = Math.ceil(items.length / itemsPerPage);
    const startIndex = (currentPage.value - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentItems = items.slice(startIndex, endIndex);

    // Extrair colunas do primeiro item, exceto invoiceUrl
    const allColumns = items.length > 0 ? Object.keys(items[0]) : [];
    const columns = allColumns.filter(col => col !== 'invoiceUrl');
    const hasInvoiceUrl = allColumns.includes('invoiceUrl');

    return (
        <div className="mt-4 p-3 bg-gray-50 dark:bg-slate-700 rounded-lg">
            <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Items Agrupados ({items.length})
                </h3>
                <span className="text-xs text-gray-600 dark:text-gray-400">
                    Página {currentPage.value} de {totalPages}
                </span>
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-600">
                    <thead className="bg-gray-100 dark:bg-slate-800">
                        <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                #
                            </th>
                            {columns.map((col) => (
                                <th key={col} className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                    {col}
                                </th>
                            ))}
                            {hasInvoiceUrl && (
                                <th className="px-3 py-2 text-center text-xs font-medium text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                                    PDF
                                </th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-600">
                        {currentItems.map((item, idx) => (
                            <tr key={startIndex + idx} className="hover:bg-gray-50 dark:hover:bg-slate-700">
                                <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-900 dark:text-white">
                                    {startIndex + idx + 1}
                                </td>
                                {columns.map((col) => (
                                    <td key={col} className="px-3 py-2 whitespace-nowrap text-xs text-gray-900 dark:text-white">
                                        {String(item[col])}
                                    </td>
                                ))}
                                {hasInvoiceUrl && (
                                    <td className="px-3 py-2 whitespace-nowrap text-center">
                                        {item.invoiceUrl ? (
                                            <a
                                                href={item.invoiceUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                                title="Abrir PDF"
                                            >
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                    <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                                                </svg>
                                            </a>
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200 dark:border-slate-600">
                    <button
                        onClick={() => currentPage.value = Math.max(1, currentPage.value - 1)}
                        disabled={currentPage.value === 1}
                        className="px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Anterior
                    </button>

                    <span className="text-xs text-gray-600 dark:text-gray-400">
                        Mostrando {startIndex + 1} - {Math.min(endIndex, items.length)} de {items.length}
                    </span>

                    <button
                        onClick={() => currentPage.value = Math.min(totalPages, currentPage.value + 1)}
                        disabled={currentPage.value === totalPages}
                        className="px-3 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Próxima
                    </button>
                </div>
            )}
        </div>
    );
};

export default NotificationPageView;
